import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Sidebar = ({
  logo,
  units,
  sidebarLinks,
  isUnitsOpen,
  setIsUnitsOpen,
  isQuickLinksOpen,
  setIsQuickLinksOpen,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Local state for which unit's dropdown is open
  const [openUnits, setOpenUnits] = useState([]);
  // 路由设置相关状态
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [password, setPassword] = useState("");
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // 初始化时从localStorage加载已授权的单元
  useEffect(() => {
    const authorizedUnits = JSON.parse(localStorage.getItem("authorizedUnits")) || [];
    setSelectedUnits(authorizedUnits);

    // 检查当前路径是否有权限访问
    checkRouteAuthorization();
  }, [location.pathname]);

  // 检查路由授权
  const checkRouteAuthorization = () => {
    const authorizedUnits = JSON.parse(localStorage.getItem("authorizedUnits")) || [];
    const path = location.pathname;

    // 如果是首页或不包含unit，则允许访问
    if (path === "/" || !path.includes("unit-")) return;

    // 提取路径中的单元号
    const unitMatch = path.match(/unit-(\d+)/);
    if (unitMatch) {
      const unitNumber = parseInt(unitMatch[1], 10);
      if (!authorizedUnits.includes(unitNumber)) {
        // 未授权，重定向到首页并显示设置窗口
        navigate("/");
        setShowRouteModal(true);
        setErrorMessage(`You do not have permission to access unit ${unitNumber}, please set route permission.`);
      }
    }
  };

  // 处理单元选择变化
  const handleUnitSelection = (unitNumber) => {
    setSelectedUnits(prev =>
      prev.includes(unitNumber)
        ? prev.filter(num => num !== unitNumber)
        : [...prev, unitNumber]
    );
  };

  // 保存路由设置
  const saveRouteSettings = () => {
    if (password === "Techblazers2025") {
      localStorage.setItem("authorizedUnits", JSON.stringify(selectedUnits));
      setShowRouteModal(false);
      setPassword("");
      setErrorMessage("");
    } else {
      setErrorMessage("Password error, please try again!");
    }
  };

  // Updated lessons for each unit (custom names)
  const unitsWithLessons = units.map((unit, idx) => {
    const number = idx + 1;
    let lessons = [];
    if (number === 1) {
      lessons = [
        { name: "Learning Goals", link: `/unit-1` },
        { name: "Intro to Parts", link: `/unit-1/lesson-1` },
        { name: "Onshape Tutorial", link: `/unit-1/lesson-2` },
        { name: "Practice", link: `/unit-1/practice` },
      ];
    } else if (number === 2) {
      lessons = [
        { name: "Learning Goals", link: `/unit-2` },
        { name: "Intro to Drivetrain", link: `/unit-2/lesson-1` },
        { name: "Build", link: `/unit-2/lesson-2` },
        { name: "Coding", link: `/unit-2/lesson-3` },
        { name: "Sensors", link: `/unit-2/lesson-4` },
        { name: "PID Control", link: `/unit-2/lesson-5` },
      ];
    } else if (number === 3) {
      lessons = [
        { name: "Learning Goals", link: `/unit-3` },
        {
          name: "Distance Sensor & Bumper Switch",
          link: `/unit-3/lesson-1`,
        },
        {
          name: "Touch LED & Optical Sensor",
          link: `/unit-3/lesson-2`,
        },
      ];
    } else if (number === 4) {
      lessons = [
        { name: "Learning Goals", link: `/unit-4` },
        { name: "Intro to Claw", link: `/unit-4/lesson-1` },
        { name: "Build", link: `/unit-4/lesson-2` },
      ];
    } else if (number === 5) {
      lessons = [
        { name: "Learning Goals", link: `/unit-5` },
        { name: "Intro to Intake Roller", link: `/unit-5/lesson-1` },
        { name: "Build", link: `/unit-5/lesson-2` },
      ];
    } else if (number === 6) {
      lessons = [
        { name: "Learning Goals", link: `/unit-6` },
        { name: "Intro to Catapult", link: `/unit-6/lesson-1` },
        { name: "Catapult Arm", link: `/unit-6/lesson-2` },
        { name: "Ratchet Motor", link: `/unit-6/lesson-3` },
        { name: "Combining Parts", link: `/unit-6/lesson-4` },
        { name: "Final Build", link: `/unit-6/lesson-5` },
      ];
    } else if (number === 7) {
      lessons = [
        { name: "Learning Goals", link: `/unit-7` },
        { name: "CAD, Build, and Test Final Robot", link: `/unit-7/lesson-1` },
        { name: "Programming Final Robot", link: `/unit-7/lesson-2` },
      ];
    } else {
      // Default: use first 2 lessons for other units
      lessons = [
        { name: "Learning Goals", link: `/unit-${number}` },
        { name: "1", link: `/unit-${number}/lesson-1` },
        { name: "2", link: `/unit-${number}/lesson-2` },
      ];
    }
    return { ...unit, number, lessons };
  });

  const handleUnitClick = (idx) => {
    setOpenUnits((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  return (
    <aside className="w-64 bg-black text-white flex flex-col h-screen shadow-2xl relative z-10">
      <div
        className="flex flex-col items-center justify-center bg-gradient-to-b from-black via-red-700 to-black p-8 flex-shrink-0 shadow-lg relative"
        style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 95% 100%, 0 100%)" }}
      >
        <Link to="/" className="flex flex-col items-center group">
          <img
            src={logo}
            alt="Techblazers Logo"
            className="h-16 w-16 object-contain mb-3 drop-shadow-[0_0_12px_rgba(239,68,68,0.5)] group-hover:scale-110 transition-transform duration-200"
          />
          <span className="text-xl font-bold tracking-wide text-white group-hover:text-red-200 transition-colors duration-200 drop-shadow">
            TECHBLAZERS
          </span>
        </Link>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pb-6 px-2">
        {/* 路由设置按钮 */}
        <div className="mt-4 px-4">
          <button
            onClick={() => setShowRouteModal(true)}
            className="w-full py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors duration-200 font-bold text-sm uppercase tracking-wider"
          >
            Access Settings
          </button>
        </div>

        {/* Units Dropdown */}
        <div className="mt-4">
          <button
            onClick={() => setIsUnitsOpen(!isUnitsOpen)}
            className="w-full flex items-center justify-between text-sm font-bold uppercase tracking-wider text-white bg-transparent hover:text-red-400 transition-all duration-200 py-3 px-4 rounded-xl mb-3 focus:outline-none sidebar-link-btn"
            style={{ border: "none" }}
          >
            <span>UNITS</span>
            <span className="transform transition-transform duration-200">
              {isUnitsOpen ? "▲" : "▼"}
            </span>
          </button>
          {isUnitsOpen && (
            <div className="mt-2 space-y-2 animate-fadeIn">
              {unitsWithLessons.map((unit, idx) => (
                <div key={unit.number} className="ml-2">
                  <button
                    className={`flex items-center w-full text-sm font-normal py-1.5 focus:outline-none whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 rounded-lg px-2 mb-1 shadow-sm uppercase tracking-wide
                      ${
                        openUnits.includes(idx)
                          ? "bg-red-900/60 text-red-200 scale-[1.03] shadow-lg"
                          : "hover:bg-red-800/80 hover:text-red-200 text-white hover:scale-[1.03] hover:shadow-lg"
                      }
                    `}
                    onClick={() => handleUnitClick(idx)}
                    title={unit.title}
                    style={{ border: "none" }}
                  >
                    <span className="truncate">{unit.title}</span>
                    <span className="ml-2 text-xs">
                      {openUnits.includes(idx) ? "▲" : "▼"}
                    </span>
                  </button>
                  {openUnits.includes(idx) && (
                    <div className="ml-4 flex flex-col space-y-1 mt-1">
                      {unit.lessons.map((lesson) => (
                        <Link
                          key={lesson.link}
                          to={lesson.link}
                          className={`text-xs pl-2 py-1 rounded hover:text-red-300 transition-all font-semibold tracking-wide uppercase sidebar-link
                            ${
                              location.pathname === lesson.link
                                ? "font-bold text-red-400"
                                : "text-white"
                            }
                          `}
                          style={{ border: "none" }}
                        >
                          {lesson.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Divider */}
        <div className="my-6"></div>
        {/* Quick Links Dropdown */}
        {/*<div>
          <button
            onClick={() => setIsQuickLinksOpen(!isQuickLinksOpen)}
            className="w-full flex items-center justify-between text-sm font-bold uppercase tracking-wider text-white bg-transparent hover:text-red-400 transition-all duration-200 py-3 px-4 rounded-xl mb-3 focus:outline-none sidebar-link-btn"
            style={{ border: "none" }}
          >
            <span>Quick Links</span>
            <span>{isQuickLinksOpen ? "▲" : "▼"}</span>
          </button>
          {isQuickLinksOpen && (
            <nav className="flex flex-col px-2 space-y-3 mt-2">
              {sidebarLinks.map((item) => (
                <a
                  key={item.link}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-4 px-2 py-3 transition-all hover:scale-105 sidebar-link"
                  style={{ border: "none" }}
                >
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="object-contain h-9 w-9"
                  />
                  <span className="text-base font-semibold text-red-100 tracking-wide uppercase hover:text-red-400 focus:text-red-400">
                    {item.name}
                  </span>
                </a>
              ))}
            </nav>
          )}
        </div>
        */}
      </div>

      {/* 路由设置模态窗口 */}
      {showRouteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-gray-900 text-white p-6 rounded-lg shadow-xl w-80">
            <h3 className="text-xl font-bold mb-4 text-red-400">Access Settings</h3>

            {errorMessage && (
              <div className="mb-4 p-2 bg-red-900/60 text-red-200 rounded">
                {errorMessage}
              </div>
            )}

            <div className="mb-4">
              <p className="mb-2 font-semibold">Select units:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {unitsWithLessons.map((unit) => (
                  <div key={unit.number} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`unit-${unit.number}`}
                      checked={selectedUnits.includes(unit.number)}
                      onChange={() => handleUnitSelection(unit.number)}
                      className="mr-2"
                    />
                    <label htmlFor={`unit-${unit.number}`}>{unit.title}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2">Password：</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Please enter password"
                className="w-full p-2 bg-gray-800 border border-gray-700 rounded text-white"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowRouteModal(false);
                  setPassword("");
                  setErrorMessage("");
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
              >
                Cancel
              </button>
              <button
                onClick={saveRouteSettings}
                className="px-4 py-2 bg-red-700 hover:bg-red-600 rounded"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
